import { CellularAchPayment, CellularAchState, CellularPayPrompt, CellularPayResult } from './types'
import { formatDate, formatMoney } from './format'
import { SUPPORT_EMAIL } from './owed-description'

// Paying by US bank account (ACH). The paywall decides: the bill counts as paid
// while a bank payment is on its way (3–5 business days) or waiting for Stripe's
// test deposits, for up to 7 days from the submit. It then says `blocking: false`.
// Only the first bank payment on a bill counts: a later one (`countsAsPaid` false)
// keeps the bill owed, and locking, until its money lands.

const PENDING: CellularAchState[] = ['waiting_deposits', 'processing']

const isPending = (state?: CellularAchState | null): boolean => !!state && PENDING.includes(state)

export const isAchOffered = (prompt: CellularPayPrompt): boolean => prompt.achEnabled === true

// A bank payment the customer already made and is waiting on.
export const pendingAch = (prompt: CellularPayPrompt | null): CellularAchPayment | null => {
  const ach = prompt?.achPayment
  return ach && isPending(ach.state) ? ach : null
}

// The pending bank payment, when it counts as paid while it clears.
export const trustedPendingAch = (prompt: CellularPayPrompt | null): CellularAchPayment | null => {
  const ach = pendingAch(prompt)
  return ach && ach.countsAsPaid !== false ? ach : null
}

const LATER_PAYMENT =
  'An earlier bank payment on this bill did not go through, so your service unlocks once this one clears.'

// A bank payment that did not go through, or got no answer within 7 days.
export const endedAch = (prompt: CellularPayPrompt | null): CellularAchPayment | null => {
  const ach = prompt?.achPayment
  return ach && (ach.state === 'failed' || ach.state === 'expired') ? ach : null
}

// The banner while a bank payment is pending.
export const achPendingMessage = (prompt: CellularPayPrompt): string => {
  const ach = pendingAch(prompt)
  const amount = formatMoney(prompt.amountDueUsd, prompt.currency)
  if (!ach) return ''
  const trusted = ach.countsAsPaid !== false
  if (ach.state === 'waiting_deposits') {
    return (
      `To finish paying ${amount}, confirm the two small deposits Stripe sent to your bank ` +
      'account (they arrive in 1–2 business days). ' +
      (trusted ? 'Your service stays on meanwhile.' : LATER_PAYMENT)
    )
  }
  if (!trusted) {
    return `Your bank payment of ${amount} is on its way. It takes 3–5 business days to clear. ${LATER_PAYMENT}`
  }
  const until = ach.trustUntil ? ` We will check again after ${formatDate(ach.trustUntil)}.` : ''
  return (
    `Your bank payment of ${amount} is on its way. It takes 3–5 business days to clear; ` +
    `your service stays on meanwhile.${until}`
  )
}

// Why the bill is owed again after a bank payment.
export const achEndedMessage = (prompt: CellularPayPrompt): string => {
  const ach = endedAch(prompt)
  if (!ach) return ''
  if (ach.state === 'expired') {
    return (
      'We have not received your bank payment yet, so this bill is due again. If your bank ' +
      'already took the money it clears by itself; otherwise please pay again.'
    )
  }
  const reason = ach.failureReason ? `: ${ach.failureReason}` : ''
  return (
    `Your bank payment did not go through${reason}. Please pay again; you can use the same ` +
    'bank account, another one, or a card.'
  )
}

// What the paywall answered when a bank payment was started.
export const isAchStarted = (result: CellularPayResult): boolean =>
  !result.paid && isPending(result.achState)

export const ACH_STARTED_MESSAGE =
  'Bank payment started. It takes 3–5 business days to clear; your service stays on meanwhile.'

export const ACH_DEPOSITS_MESSAGE =
  'Bank account added. Confirm the two small deposits Stripe sends to it in 1–2 business days ' +
  'to finish paying; your service stays on meanwhile.'

export const ACH_STARTED_LATER_MESSAGE = `Bank payment started. It takes 3–5 business days to clear. ${LATER_PAYMENT}`

export const ACH_DEPOSITS_LATER_MESSAGE =
  'Bank account added. Confirm the two small deposits Stripe sends to it in 1–2 business days ' +
  `to finish paying. ${LATER_PAYMENT}`

export const achStartedMessage = (result: CellularPayResult): string => {
  const later = result.achCountsAsPaid === false
  if (result.achState === 'waiting_deposits') {
    return later ? ACH_DEPOSITS_LATER_MESSAGE : ACH_DEPOSITS_MESSAGE
  }
  return later ? ACH_STARTED_LATER_MESSAGE : ACH_STARTED_MESSAGE
}

// Stripe needs the account holder's name to add a bank account.
export const isHolderNameReady = (name: string): boolean => name.trim().length > 1

// The business name Stripe shows the customer in its bank window.
const ACH_MERCHANT_NAME = 'Cirrus Systems, Inc.'

// Stripe's required ACH debit authorization, shown before the customer pays by bank.
export const achMandateText = (amount: string): string =>
  `By clicking Pay ${amount}, you authorize ${ACH_MERCHANT_NAME} to debit the bank account ` +
  'you choose for this payment and, when your cellular service renews on it, for future ' +
  'payments according to your subscription, until you change your payment method. You may ' +
  `cancel this authorization at any time by contacting ${SUPPORT_EMAIL}.`
